"use client";

import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { intFmt } from "../chart-formatters";
import { useLegendItem } from "./legend-context";

export interface LegendValueProps {
  /** Value class name. Default: "text-sm tabular-nums" */
  className?: string;
  /** Show percentage alongside value. Default: false */
  showPercentage?: boolean;
  /** Percentage class name. Default: "text-xs tabular-nums" */
  percentageClassName?: string;
  /** Format function for numeric values. Ignored for string values. Default: intFmt */
  formatValue?: (value: number) => string;
  /** Format function for percentage. Default: (p) => `${p.toFixed(0)}%` */
  formatPercentage?: (percentage: number) => string;
}

export function LegendValue({
  className = "text-sm tabular-nums",
  showPercentage = false,
  percentageClassName = "text-xs tabular-nums",
  formatValue,
  formatPercentage = (p) => `${p.toFixed(0)}%`,
}: LegendValueProps) {
  const { item, percentage } = useLegendItem();
  const { intlLocale } = useViewerLanguage();
  const displayValue =
    typeof item.value === "number"
      ? formatValue
        ? formatValue(item.value)
        : intFmt(item.value, intlLocale)
      : item.value;
  const showPct =
    showPercentage &&
    ((typeof item.value === "number" && item.maxValue) ||
      item.percentage !== undefined);

  return (
    <span
      className={cn(
        "flex items-center gap-2 text-legend-muted-foreground",
        className,
      )}
    >
      <span>{displayValue}</span>
      {showPct && (
        <span className={percentageClassName}>
          {formatPercentage(percentage)}
        </span>
      )}
    </span>
  );
}

LegendValue.displayName = "LegendValue";
